import { useEffect, useRef, useState } from 'react';

type ListTableCellTruncation = {
  ref: React.RefObject<HTMLSpanElement>;
  maxWidth: number | undefined;
  isTruncated: boolean;
};

export const useListTableCellTruncation = (): ListTableCellTruncation => {
  const ref = useRef<HTMLSpanElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const span = ref.current;
    if (!span) {
      return;
    }

    const td = span.closest('td');
    if (!td) {
      return;
    }

    const updateWidth = () => {
      const row = td.closest('tr');
      const table = td.closest('table');
      if (!row || !table) {
        return;
      }

      const tableWidth = table.clientWidth;
      const cells = Array.from(row.children) as Array<HTMLElement>;
      const tdIndex = cells.indexOf(td);

      const otherColumnsWidth = cells.reduce((sum, cell, i) => {
        return i === tdIndex ? sum : sum + cell.getBoundingClientRect().width;
      }, 0);

      const cellAvailableWidth = tableWidth - otherColumnsWidth;

      const tdRect = td.getBoundingClientRect();
      const spanRect = span.getBoundingClientRect();
      const leftOffset = spanRect.left - tdRect.left;
      const tdPaddingRight = 12;
      const availableWidth = cellAvailableWidth - leftOffset - tdPaddingRight;

      setMaxWidth(availableWidth);
      setIsTruncated(span.scrollWidth > availableWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(td);
    return () => observer.disconnect();
  }, []);

  return { ref, maxWidth, isTruncated };
};
